import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { Modal } from '@/pages/operacio/comu';

/**
 * Panell lateral dret (calaix) per crear elements: deixa veure la pantalla de darrere i diu
 * on es crearà l'element (`on`). Es tanca amb Esc o clicant fora.
 */
export function Calaix({
  titol,
  etiqueta,
  on,
  onTanca,
  peu,
  children,
  ample,
}: {
  titol: string;
  /** Text petit sobre el títol («Nou servei»). */
  etiqueta?: string;
  /** On es crearà: ruta de la jerarquia ja formatada. */
  on?: ReactNode;
  onTanca: () => void;
  peu?: ReactNode;
  children: ReactNode;
  ample?: boolean;
}) {
  const { t } = useTranslation('administracio');
  const panell = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onTanca();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onTanca]);
  useEffect(() => {
    const primer = panell.current?.querySelector<HTMLElement>(
      'input:not([disabled]), textarea, button.desplegable, select',
    );
    primer?.focus();
  }, []);
  return (
    <div className="calaix-fons" onMouseDown={(e) => e.target === e.currentTarget && onTanca()}>
      <div
        ref={panell}
        className={`calaix${ample ? ' calaix--ample' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={titol}
      >
        <div className="calaix__cap">
          <div className="calaix__titols">
            {etiqueta ? <span className="calaix__etiqueta">{etiqueta}</span> : null}
            <h2>{titol}</h2>
          </div>
          <button
            type="button"
            className="modal__tanca"
            onClick={onTanca}
            aria-label={t('marc.tanca')}
            title={t('marc.tanca')}
          >
            <Icona nom="xmark" />
          </button>
          {on ? <div className="calaix__on">{on}</div> : null}
        </div>
        <div className="calaix__cos form">{children}</div>
        {peu ? <div className="calaix__peu">{peu}</div> : null}
      </div>
    </div>
  );
}

/**
 * Confirmació d'una acció irreversible o rellevant (desactivar, treure, retirar…).
 * El botó d'acció és vermell si `perill`.
 */
export function Confirmacio({
  titol,
  children,
  textAccio,
  perill,
  ocupat,
  onConfirma,
  onTanca,
}: {
  titol: string;
  children: ReactNode;
  textAccio: string;
  perill?: boolean;
  ocupat?: boolean;
  onConfirma: () => void;
  onTanca: () => void;
}) {
  const { t } = useTranslation('administracio');
  return (
    <Modal
      titol={titol}
      icona={
        <Icona
          nom={perill ? 'exclamationmark-triangle' : 'info-circle'}
          className={`${perill ? 'c-red' : 'c-blue'} icon-20`}
        />
      }
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('marc.confirma.cancella')}
          </button>
          <button
            type="button"
            className={`btn btn--s ${perill ? 'btn--critic' : 'btn--dark'}`}
            disabled={ocupat}
            onClick={onConfirma}
          >
            {textAccio}
          </button>
        </>
      }
    >
      <div className="confirmacio__text">{children}</div>
    </Modal>
  );
}
